import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Container } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import WarehouseClientTable from "../components/WarehouseClientTable";
import { useWarehouseClient } from "../../../apis/warehouseClient/WarehouseClientProvider";
import { getListaWarehouseClientPaginated } from "../../../services/warehouseClientService";
import { exportarParaExcel } from "../../../utils/exportarExcel";
import ExportExcelButton from "../../../components/ExportExcelButton";
import { getPageName, getUiInterface } from "../../../locales/translationUtils";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { GridColDef, GridSortModel } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";
import Paginacao from "../../../types/paginacao";
import { FaEdit } from "react-icons/fa";

const WarehouseClientView: React.FC = () => {
    const { t } = useTranslation();
    const { warehouseClient, fetchWarehouseClientPaginated, apiResponse } = useWarehouseClient();
    const navigate = useNavigate();
    const location = useLocation();
    const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" | "warning" | "info" } | null>(null);

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 40,
        page: 0,
    });

    const [paginacao, setPaginacao] = React.useState({
        pageNum: 0,
        quantityPerPage: 40
    } as Paginacao);

    const [sortModel, setSortModel] = React.useState<GridSortModel>([]);

    useEffect(() => {
        const newPaginacao: Paginacao = {
            ...paginacao,
            pageNum: paginationModel.page,
            quantityPerPage: paginationModel.pageSize,
            sort: sortModel[0]?.field,
            order: sortModel[0]?.sort ?? undefined,
        };
        setPaginacao(newPaginacao);
        fetchWarehouseClientPaginated(newPaginacao);
    }, [paginationModel, sortModel]);

    useEffect(() => {
        if (location.state?.message) {
            setAlert({ message: location.state.message, variant: location.state.variant });

            navigate(location.pathname, { replace: true });

            const timeout = setTimeout(() => setAlert(null), 5000);
            return () => clearTimeout(timeout);
        }
    }, [location, navigate]);

    const [exportando, setExportando] = useState(false);

    const handleExportarExcel = async () => {
        setExportando(true);

        try {
            const resposta = await getListaWarehouseClientPaginated({
                pageNum: 0,
                quantityPerPage: 100000,
                sort: sortModel[0]?.field,
                order: sortModel[0]?.sort,
            } as Paginacao);

            const linhas = (resposta?.data?.values ?? []).map((row: any) => ({
                [t('warehouseClient.fields.id')]: row.id,
                [t('warehouseClient.fields.client')]: row.client?.name ?? '',
                [t('warehouseClient.fields.wr')]: row.wr?.name ?? '',
            }));

            if (!exportarParaExcel(linhas, 'warehouse_client')) {
                setAlert({ message: getUiInterface('exportEmptyResult'), variant: 'warning' });
            }
        } catch (error) {
            setAlert({ message: getUiInterface('exportError'), variant: 'danger' });
        } finally {
            setExportando(false);
        }
    };

    const navItems: NavBarItem[] = [
        { label: t('warehouseClient.labels.home'), route: '/', iconName: 'house-door' },
        { label: t('warehouseClient.labels.new'), route: '/warehouseclient/create', iconName: 'plus-circle' },
    ];

    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: t('warehouseClient.fields.id'),
            width: 70,
            renderCell: (params) => (
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    color: "black",
                    cursor: "pointer"
                }}
                onClick={() => navigate(`/warehouseClient/${params.row.id}`)}>
                    <FaEdit className="text-primary" style={{ marginRight: "6px", marginLeft: "2px" }}/>
                    {params.row.id}
                </span>
            ),
        },
        {
            field: 'client',
            headerName: t('warehouseClient.fields.client'),
            width: 200,
            renderCell: (params) => params.row.client?.name
        },
        {
            field: 'wr',
            headerName: t('warehouseClient.fields.wr'),
            width: 200,
            renderCell: (params) => params.row.wr?.name
        },
    ];

    return (
         <Container fluid style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getPageName("warehouseClient")}</h2>
            <div className="mb-3"></div>
            <div>
                {alert && (
                    <AlertOverlay
                        message={alert.message}
                        variant={alert.variant}
                        onClose={() => setAlert(null)}
                    />
                )}
            </div>
            <div className="d-flex justify-content-end mb-2">
                <ExportExcelButton onClick={handleExportarExcel} exportando={exportando} />
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
                <WarehouseClientTable
                    rows={warehouseClient || []}
                    columns={columns}
                    getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                    apiResponse={apiResponse}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                />
            </div>
        </Container>
    );
};

export default WarehouseClientView;